import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { FaGlobe, FaMagic } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import "./ContactUs.css";
import BaseUrl from "../ApiServices/BaseUrl";
import { JOB_SEEKER_CATEGORY_OPTIONS, COUNTRY_NATIONALITIES } from "../../Constants/dropdownOptions";
import {
  buildSupportMobileNumber,
  normalizeCountryCode,
  splitPhoneNumber,
  validateSupportForm,
} from "../../Constants/countryDialCodes";
import JobSeekerNavbar from "../Pages/Navbar/Navbar";
import EmployerNavbar from "../Pages/Navbar/EmployerNavbar";
import EmployeeNavbar from "../Pages/Navbar/JobSeekerNavbar";
import AgencyNavbar from "../Pages/Navbar/AgencyNavbar";
import AdminNavbar from "../Pages/Navbar/AdminNavbar";
import Footer from "../Footer/Footer";
import CustomDropdown from "./CustomDropdown";
import CountryCodeSelect from "../Common/CountryCodeSelect/CountryCodeSelect";

const storedUser = JSON.parse(localStorage.getItem("user")) || {};
const isLoggedIn = storedUser && storedUser.id;

const renderNavbar = () => {
  if (storedUser.user_type === "employer") return <EmployerNavbar />;
  if (storedUser.user_type === "job seeker") return <EmployeeNavbar />;
  if (storedUser.user_type === "agency") return <AgencyNavbar />;
  if (storedUser.user_type === "admin") return <AdminNavbar />;
  return <JobSeekerNavbar />;
};

const getDefaultUserRole = () => {
  if (storedUser.user_type === "employer") return "employer";
  if (storedUser.user_type === "job seeker") return "job_seeker";
  if (storedUser.user_type === "agency") return "agency";
  return "employer"; // Default for non-logged-in
};

const getAutoFillData = () => {
  if (!isLoggedIn) return { name: "", email: "", mobile_country_code: "", mobile_number: "" };

  const phoneData = splitPhoneNumber(
    storedUser.mobile_number,
    storedUser.mobile_number_country_code,
    storedUser.mobile_country_code,
    storedUser.whatsapp_country_code
  );

  return {
    name: (storedUser.first_name + (storedUser.last_name ? " " + storedUser.last_name : "")) || "",
    email: storedUser.email || "",
    mobile_country_code: phoneData.countryCode || "",
    mobile_number: phoneData.localNumber || "",
  };
};

const messageTemplates = [
  { label: "General Employer", text: "Hello, we are a company seeking skilled and qualified workers. We are interested in connecting with verified manpower agencies, exploring hiring options, and subscribing to GulfWorker services." },
  { label: "Domestic Worker Employer", text: "Hello, we are looking to hire housemaids, nannies, and caregivers. We would like to connect with trusted manpower agencies, review candidate profiles, and subscribe to GulfWorker for easy and fast hiring." },
  { label: "Subscription Inquiry", text: "Hello, we would like to know more about your subscription plans and how we can start hiring through GulfWorker." },
  { label: "Urgent Hiring – Skilled Workers", text: "Hello, I need skilled workers urgently for my company." },
  { label: "Urgent Hiring – Domestic Workers", text: "Hello, I need domestic workers urgently." },
  { label: "Standard Hiring", text: "Hello, I am looking to hire workers." },
  { label: "Bulk Hiring", text: "Hello, I require multiple workers for my company." },
];

const ContactUs = () => {
  const { t } = useTranslation();
  const autoFill = getAutoFillData();

  const resolvedTemplates = [
    { label: t("contact_form.templates.general.label"), text: t("contact_form.templates.general.text") },
    { label: t("contact_form.templates.domestic.label"), text: t("contact_form.templates.domestic.text") },
    { label: t("contact_form.templates.subscription.label"), text: t("contact_form.templates.subscription.text") },
    { label: t("contact_form.templates.urgent_skilled.label"), text: t("contact_form.templates.urgent_skilled.text") },
    { label: t("contact_form.templates.urgent_domestic.label"), text: t("contact_form.templates.urgent_domestic.text") },
    { label: t("contact_form.templates.standard.label"), text: t("contact_form.templates.standard.text") },
    { label: t("contact_form.templates.bulk.label"), text: t("contact_form.templates.bulk.text") },
  ];

  const [formData, setFormData] = useState({
    name: autoFill.name,
    email: autoFill.email,
    mobile_country_code: autoFill.mobile_country_code,
    mobile_number: autoFill.mobile_number,
    message: "",
    user_role: getDefaultUserRole(),
    job_category: "",
    nationality: "",
    promotionalConsent: false,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showNatDropdown, setShowNatDropdown] = useState(false);
  const [natSearch, setNatSearch] = useState("");
  const [isCodePopupShown, setIsCodePopupShown] = useState(false);
  const natDropdownRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (natDropdownRef.current && !natDropdownRef.current.contains(e.target)) {
        setShowNatDropdown(false);
        setNatSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePhoneFocus = () => {
    if (!formData.mobile_country_code && !isCodePopupShown) {
      setIsCodePopupShown(true);
      setErrors((prev) => ({ ...prev, mobile_country_code: t("contact_form.error_selection_required") }));
      Swal.fire({
        title: t("contact_form.error_country_code_title"),
        text: t("contact_form.error_country_code_text"),
        icon: "warning",
        confirmButtonColor: "#3085d6",
      }).then(() => {
        setTimeout(() => setIsCodePopupShown(false), 500);
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : name === "mobile_number" ? value.replace(/\D/g, "") : value,
    });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleTemplateSelect = (text) => {
    setFormData({ ...formData, message: text });
    setErrors((prev) => ({ ...prev, message: "" }));
    setShowTemplates(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateSupportForm(formData);

    if (!formData.promotionalConsent) {
      validationErrors.promotionalConsent = t("contact_form.error_consent_required");
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        mobile_country_code: normalizeCountryCode(formData.mobile_country_code),
        mobile_number: buildSupportMobileNumber(formData.mobile_country_code, formData.mobile_number),
      };

      const response = await fetch(`${BaseUrl}/supporttickets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        Swal.fire({
          title: t("contact_form.success_title"),
          text: t("contact_form.success_text"),
          icon: "success",
          confirmButtonColor: "#3085d6",
        });
        setFormData((prev) => ({ ...prev, message: "", job_category: "", nationality: "", promotionalConsent: false }));
        setErrors({});
      } else {
        throw new Error("Failed to submit form");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      Swal.fire({ title: t("contact_form.error_title"), text: t("contact_form.error_text"), icon: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {renderNavbar()}
      <section className="contact-us">
        <Container>
          {/* Header Info Section */}
          <Row className="contact-box">
            <Col>
              <h4 className="contact-title">{t("contact_us_page.title")}</h4>
              <p className="contact-text">
                {t("contact_us_page.description")}
              </p>
            </Col>
          </Row>

          <Row className="form-map-section">
            <Col md={6}>
              <div className="map-box h-100">
                <iframe
                  title="Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3771.663361947527!2d73.00846147426734!3d19.034550053274298!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c3c196a0cf43%3A0x153680f6faba482f!2sGudnet%20Computers%20Pvt.%20Ltd.!5e0!3m2!1sen!2sin!4v1774682550088!5m2!1sen!2sin"
                  width="100%" height="100%" style={{ border: 0, minHeight: '400px' }} 
                  allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </Col>

            <Col md={6}>
              <div className="form-box">
                <h5 className="form-title">{t("contact_us_page.get_in_touch")}</h5>
                <Form onSubmit={handleSubmit} noValidate>
                  {/* Field 1: User Role Dropdown */}
                  <Form.Group className="mb-3" controlId="formRole">
                    <CustomDropdown
                      name="user_role"
                      value={formData.user_role}
                      onChange={handleInputChange}
                      options={[
                        { value: "employer", label: t("contact_form.role_employer") },
                        { value: "agency", label: t("contact_form.role_agency") },
                        { value: "job_seeker", label: t("contact_form.role_job_seeker") },
                      ]}
                      required
                      invalid={Boolean(errors.user_role)}
                    />
                    {errors.user_role && <div className="invalid-feedback d-block">{errors.user_role}</div>}
                  </Form.Group>

                  {/* Field 2 & 3: Category & Nationality (Single Row) */}
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="formJobCategory">
                        <Form.Select
                          name="job_category"
                          value={formData.job_category}
                          onChange={handleInputChange}
                          isInvalid={Boolean(errors.job_category)}
                          style={{ height: '50px' }}
                        >
                          <option value="">{t("contact_form.select_category")}</option>
                          {JOB_SEEKER_CATEGORY_OPTIONS.map((cat, idx) => (
                            <option key={idx} value={cat}>{cat}</option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{errors.job_category}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="formNationality">
                        <div ref={natDropdownRef} style={{ position: 'relative' }}>
                          <div
                            className={`form-select ${errors.nationality ? "is-invalid" : ""}`}
                            onClick={() => setShowNatDropdown(!showNatDropdown)}
                            style={{ cursor: 'pointer', height: '50px', display: 'flex', alignItems: 'center', backgroundColor: '#fff', border: errors.nationality ? '1px solid #dc3545' : '1px solid #ced4da', color: formData.nationality ? '#212529' : '#6c757d' }}
                          >
                            {formData.nationality || t("contact_form.select_nationality")}
                          </div>
                          {showNatDropdown && (
                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1050, background: '#fff', border: '1px solid #ced4da', borderRadius: '0.375rem', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', marginTop: '2px' }}>
                              <div style={{ padding: '8px' }}>
                                <input
                                  type="text" className="form-control form-control-sm"
                                  placeholder={t("contact_form.search")} value={natSearch}
                                  onChange={(e) => setNatSearch(e.target.value)} autoFocus
                                />
                              </div>
                              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                {COUNTRY_NATIONALITIES.filter(n => n.toLowerCase().includes(natSearch.toLowerCase())).map((nat, idx) => (
                                  <div
                                    key={idx} onClick={() => {
                                      setFormData({ ...formData, nationality: nat });
                                      setErrors(prev => ({ ...prev, nationality: "" }));
                                      setShowNatDropdown(false);
                                      setNatSearch("");
                                    }}
                                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '14px', borderBottom: '1px solid #f0f0f0' }}
                                    onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                  >
                                    {nat}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        {errors.nationality && <div className="invalid-feedback d-block">{errors.nationality}</div>}
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Field 4: Name */}
                  <Form.Group className="mb-3" controlId="formName">
                    <Form.Control
                      type="text" name="name" placeholder={t("contact_form.name_placeholder")}
                      value={formData.name} onChange={handleInputChange}
                      isInvalid={Boolean(errors.name)} required style={{ height: '50px' }}
                    />
                    <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                  </Form.Group>

                  {/* Field 5 & 6: Country Code & WhatsApp Number (Single Row) */}
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="formPhoneCode">
                        <CountryCodeSelect
                          value={formData.mobile_country_code}
                          onChange={(value) => {
                            setFormData(prev => ({ ...prev, mobile_country_code: value }));
                            setErrors(prev => ({ ...prev, mobile_country_code: "" }));
                          }}
                          error={errors.mobile_country_code}
                          style={{ height: '50px' }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="formPhoneNumber">
                        <Form.Control
                          type="text" name="mobile_number" inputMode="numeric"
                          placeholder={t("contact_form.phone_placeholder")} value={formData.mobile_number}
                          onChange={handleInputChange} onFocus={handlePhoneFocus}
                          isInvalid={Boolean(errors.mobile_number)}
                          required style={{ height: '50px' }}
                        />
                        <Form.Control.Feedback type="invalid">{errors.mobile_number}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Field 7: Email */}
                  <Form.Group className="mb-3" controlId="formEmail">
                    <Form.Control
                      type="email" name="email" placeholder={t("contact_form.email_placeholder")}
                      value={formData.email} onChange={handleInputChange}
                      isInvalid={Boolean(errors.email)} required style={{ height: '50px' }}
                    />
                    <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                  </Form.Group>

                  {/* Field 8: Message */}
                  <Form.Group className="mb-3" controlId="formMessage">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Form.Label className="mb-0">{t("contact_form.message_label")}</Form.Label>
                      <Button
                        variant="outline-primary" size="sm" type="button"
                        onClick={() => setShowTemplates(!showTemplates)}
                        style={{ borderRadius: '20px', fontSize: '12px' }}
                      >
                        <FaMagic className="me-1" /> {t("contact_form.auto_description")}
                      </Button>
                    </div>

                    {showTemplates && (
                      <div className="message-templates mb-2 p-2 border rounded bg-white shadow-sm" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {resolvedTemplates.map((template, index) => (
                          <div
                            key={index} className="p-2 border-bottom cursor-pointer hover-bg-light"
                            onClick={() => handleTemplateSelect(template.text)}
                            style={{ cursor: 'pointer' }}
                          >
                            <strong className="d-block small text-primary">{template.label}</strong>
                            <span className="small text-muted">{template.text}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <Form.Control
                      as="textarea" rows={4} name="message"
                      placeholder={t("contact_form.message_placeholder")}
                      value={formData.message} onChange={handleInputChange}
                      isInvalid={Boolean(errors.message)} required
                    />
                    <Form.Control.Feedback type="invalid">{errors.message}</Form.Control.Feedback>
                  </Form.Group>

                  {/* Promotional Messages Consent Checkbox */}
                  <Form.Group className="mb-3" controlId="formPromotional">
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      <input
                        type="checkbox"
                        name="promotionalConsent"
                        checked={formData.promotionalConsent}
                        onChange={handleInputChange}
                        style={{ marginTop: '4px', cursor: 'pointer', width: '18px', height: '18px', flexShrink: 0 }}
                        id="promotionalCheckbox"
                      />
                      <label htmlFor="promotionalCheckbox" style={{ margin: 0, cursor: 'pointer', userSelect: 'none', fontSize: '13px' }}>
                        {t("contact_form.consent_part1")}{" "}
                        <a
                          href="/terms-and-conditions"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#0056b3', textDecoration: 'none', fontWeight: '600' }}
                          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                        >
                          {t("contact_form.terms_link")}
                        </a>
                        {" "}{t("contact_form.consent_part2")}{" "}
                        <a
                          href="/privacy-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#0056b3', textDecoration: 'none', fontWeight: '600' }}
                          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                        >
                          {t("contact_form.privacy_link")}
                        </a>
                        .
                      </label>
                    </div>
                    {errors.promotionalConsent && <div className="invalid-feedback d-block" style={{ marginTop: '5px' }}>{errors.promotionalConsent}</div>}
                  </Form.Group>

                  {/* Submit Button */}
                  <Button
                    variant="primary"
                    type="submit"
                    className="w-100"
                    disabled={isSubmitting || !formData.promotionalConsent}
                    title={!formData.promotionalConsent ? t("contact_form.tooltip_consent") : ""}
                    style={{ height: '50px', fontSize: '18px', fontWeight: 'bold' }}
                  >
                    {isSubmitting ? t("contact_form.sending") : t("contact_form.submit")}
                  </Button>
                </Form>
              </div>
            </Col>
          </Row>

          {/* Footer Info Row */}
          <Row className="mt-5">
            <Col md={12}>
              <div className="info-card2">
                <FaGlobe className="info-icon" />
                <p className="info-text">
                  {t("contact_us_page.we_serve")}
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <Footer />
    </>
  );
};

export default ContactUs;
